import React, { useState } from 'react';
import { cn } from '@core/lib/utils';
import { useChatCopy } from '../../config';
import type { MemoryRecallPayload } from '../services/stevensV4Service';

/**
 * #1308 (CP-20, C-10): the quiet recall line — rendered above an agent reply
 * only when the backend marked the turn as materially memory-shaped (the
 * `memory_recall` event is emitted only on material retrieval; this component
 * never re-decides materiality).
 *
 * Construction per grammar §14.7: not a speech turn — no bubble, no speaker
 * position; a thin left rule with muted small text. "Inspectable": when the
 * payload names sources, a disclosure toggle reveals what was recalled.
 */
interface RecallLineProps {
  recall: MemoryRecallPayload;
  className?: string;
}

export const RecallLine = React.memo(function RecallLine({ recall, className }: RecallLineProps) {
  const [expanded, setExpanded] = useState(false);
  const copy = useChatCopy().recall;
  const hasSources = Boolean(recall.sources && recall.sources.length > 0);

  return (
    // #1303 preset-contract keys — §14.7's construction: a thin left rule in
    // the signal register (memory references), quiet ink-2 text.
    <div
      className={cn('my-2 border-l-[3px] border-signal pl-3 text-caption text-chat-ink-2', className)}
      role="note"
    >
      <span>{copy.line(recall.sourceCount)}</span>
      {hasSources && (
        <>
          {' '}
          <button
            type="button"
            className="underline underline-offset-2 hover:text-chat-ink focus:outline-none focus:ring-2 focus:ring-focus-outer rounded-sm"
            aria-expanded={expanded}
            onClick={() => setExpanded((current) => !current)}
          >
            {expanded ? copy.hideDetails : copy.showDetails}
          </button>
          {expanded && (
            <ul className="mt-1 list-disc pl-4">
              {recall.sources!.map((source, index) => (
                <li key={`${source.kind}-${index}`}>{source.label}</li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
});
